/** The app's themes, and the one place their colours are read from.
 *
 *  The five Waki family themes (2026-10-02): Mac, Boardroom, Graphite, Civic
 *  and Cobalt, the same five every Waki app offers. They are defined once, in
 *  waki-themes (family/family-themes.mjs), published as family.json, and
 *  written into ./familyThemes.generated.ts by scripts/sync-family-themes.mjs
 *  (`npm run themes:sync`). Do not edit colours here or there: change
 *  waki-themes and sync.
 *
 *  Each theme is two eleven-step scales:
 *
 *    neutral  drives Tailwind's `slate-*` (surfaces, borders, text)
 *    accent   drives Tailwind's `sky-*`   (buttons, links, selection)
 *
 *  tailwind.config.js points both families at CSS variables (`--wov-n-<step>`,
 *  `--wov-a-<step>`), and `themeStylesheet()` below defines those variables per
 *  theme. The app is written almost entirely in `slate` and `sky` utilities with
 *  `dark:` variants, so remapping the two scales is what makes a theme change
 *  the whole interface rather than the handful of surfaces that wear a semantic
 *  class. The scales do not change with the mode: `dark:` variants already pick
 *  the step, so a scale is one ramp from light to dark in both modes.
 *
 *  Boardroom is WOV's old Slate and Graphite its old Paper, unchanged; Mac is
 *  its old grey Graphite neutrals with the macOS blue. Civic, the glass theme,
 *  also paints its canvas gradient and uses its amber second accent for small
 *  highlights (see `themeStylesheet`).
 *
 *  `themePalette()` derives the named roles (`--waki-*`, the same names the Waki
 *  Mac apps use) from the scales for each mode, for CSS that is not Tailwind
 *  and for the picker's miniature previews. Previews and the live app therefore
 *  read the same values.
 *
 *  The stylesheet is generated at build time (see the wov-themes plugin in
 *  vite.config.ts), so every theme is plain static CSS and nothing is injected
 *  at runtime. `test/themes.test.ts` holds every theme to the contrast bar in
 *  both modes.
 *
 *  Pure data and string building only: no DOM, no React. It is imported by
 *  vite.config.ts and by the Node tests as well as by the app. */

import { FAMILY_THEMES, WOV_MIGRATION } from "./familyThemes.generated.js";

export type ThemeId = (typeof FAMILY_THEMES)[number]["id"];

export const SCALE_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ScaleStep = (typeof SCALE_STEPS)[number];
export type Scale = Readonly<Record<ScaleStep, string>>;

/** A family role table for one mode (`bg-1`, `panel`, `accent-2`, ...). */
export type FamilyRoles = Readonly<Record<string, string>>;

export interface ThemeDef {
  id: ThemeId;
  name: string;
  description: string;
  /** The family's material: "glass" paints the canvas gradient. */
  material: string | null;
  /** Drives Tailwind `slate-*`. */
  neutral: Scale;
  /** Drives Tailwind `sky-*`. */
  accent: Scale;
  /** The family's named roles per mode, as the Waki Mac apps paint them. */
  roles: { light: FamilyRoles; dark: FamilyRoles };
  webId: string;
  migratesFrom: readonly string[];
}

function toScale(o: Readonly<Record<string, string>>): Scale {
  return Object.fromEntries(SCALE_STEPS.map((s) => [s, o[String(s)]])) as unknown as Scale;
}

/** In family order: mac, boardroom, graphite, civic, cobalt. */
export const THEMES: readonly ThemeDef[] = FAMILY_THEMES.map((t) => ({
  id: t.id,
  name: t.name,
  description: t.description,
  material: t.material,
  neutral: toScale(t.neutral),
  accent: toScale(t.accent),
  roles: { light: t.roles.light, dark: t.roles.dark },
  webId: t.webId,
  migratesFrom: t.migratesFrom,
}));

export const DEFAULT_THEME_ID: ThemeId = "boardroom";

export function isThemeId(v: unknown): v is ThemeId {
  return typeof v === "string" && THEMES.some((t) => t.id === v);
}

export function getThemeDef(id: ThemeId): ThemeDef {
  return THEMES.find((t) => t.id === id) ?? THEMES.find((t) => t.id === DEFAULT_THEME_ID)!;
}

/** A theme id as stored now (the five), anything else read as an old id. */
export function migrateThemeId(stored: string | null | undefined): ThemeId {
  if (isThemeId(stored)) return stored;
  return migrateLegacyThemeId(stored);
}

/** An id stored before the family themes, onto the five.
 *
 *  WOV's own three first (family.json `wovMigration`): Slate is Boardroom,
 *  Paper is Graphite, and the old grey Graphite is Mac. Then the family's own
 *  ids, web ids and the ids it migrates from other Waki apps (`glass` is
 *  Civic, `studio` Cobalt). Then the old 37-theme catalog as before: neutral
 *  and monochrome looks went to the old Graphite (now Mac), warm and editorial
 *  ones to Paper (now Graphite), everything else to Slate (now Boardroom). The
 *  neutral check runs first so `editorial-noir` resolves by its colour. */
export function migrateLegacyThemeId(stored: string | null | undefined): ThemeId {
  if (!stored) return DEFAULT_THEME_ID;
  const own = (WOV_MIGRATION as Readonly<Record<string, string>>)[stored];
  if (isThemeId(own)) return own;
  const exact = THEMES.find((t) => t.id === stored || t.webId === stored || t.migratesFrom.includes(stored));
  if (exact) return exact.id;
  const s = stored.toLowerCase();
  const neutral = ["clean-light", "clean-dim", "graphite", "noir", "mac", "minimal", "nord"];
  if (neutral.some((k) => s.includes(k))) return WOV_MIGRATION.graphite;
  const warm = ["warm", "editorial", "paper", "sunrise", "copper", "rose", "sakura", "sunset"];
  if (warm.some((k) => s.includes(k))) return WOV_MIGRATION.paper;
  return DEFAULT_THEME_ID;
}

/* ---- colour arithmetic --------------------------------------------------- */

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16)) as [number, number, number];
}

function toHex(rgb: readonly number[]): string {
  return "#" + rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")).join("");
}

/** `rgba()` of a hex colour. */
export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Linear mix of two hex colours; `t` is the share of `b`. */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return toHex(x.map((c, i) => c + (y[i] - c) * t));
}

/** WCAG relative luminance of a hex colour. */
export function relativeLuminance(hex: string): number {
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex).map(lin);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two hex colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const x = relativeLuminance(a);
  const y = relativeLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/* ---- named roles --------------------------------------------------------- */

/** The named colours of one theme in one mode: the `--waki-*` roles the Waki
 *  Mac apps use, plus the ink that reads on the accent. */
export interface ThemePalette {
  /** Page canvas. */
  bg1: string;
  /** Second canvas tone (canvas gradients, recessed areas). */
  bg2: string;
  bg3: string;
  /** Opaque panel: cards, list rows, sheets. */
  panel: string;
  /** Raised panel: popovers, menus, the panels in a preview. */
  panel2: string;
  /** Inset: segmented-control tracks, wells. */
  panel3: string;
  /** Chrome: the sidebar and top/bottom bars. */
  bar: string;
  border: string;
  border2: string;
  text: string;
  muted: string;
  accent: string;
  accent2: string;
  focus: string;
  shadow: string;
  /** Text colour that reads on `accent`. */
  onAccent: string;
}

/** Whichever of white or the theme's darkest neutral reads better on `accent`. */
function inkOn(accent: string, darkInk: string): string {
  return contrastRatio("#ffffff", accent) >= contrastRatio(darkInk, accent) ? "#ffffff" : darkInk;
}

/** A family role colour as a hex: a hex as is, or `color-mix(in srgb, #a p%,
 *  #b)` worked out. null for anything else (an rgba(), a variable). */
export function resolveRoleColor(value: string | undefined): string | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  const m = /^color-mix\(in srgb,\s*(#[0-9a-f]{6})\s+([\d.]+)%,\s*(#[0-9a-f]{6})\s*\)$/.exec(v);
  if (m) return mix(m[1], m[3], 1 - Number(m[2]) / 100);
  return null;
}

/** The glass theme (Civic) paints its canvas as a gradient of its own two
 *  background tones and uses its second accent (amber) for small highlights;
 *  the other themes are flat, from their scales. */
export function paintsCanvasGradient(theme: ThemeDef): boolean {
  return theme.material === "glass";
}

export function themePalette(theme: ThemeDef, dark: boolean): ThemePalette {
  const p = scalePalette(theme, dark);
  if (!paintsCanvasGradient(theme)) return p;
  const r = dark ? theme.roles.dark : theme.roles.light;
  return {
    ...p,
    bg1: resolveRoleColor(r["bg-1"]) ?? p.bg1,
    bg2: resolveRoleColor(r["bg-2"]) ?? p.bg2,
    accent2: resolveRoleColor(r["accent-2"]) ?? p.accent2,
  };
}

function scalePalette(theme: ThemeDef, dark: boolean): ThemePalette {
  const n = theme.neutral;
  const a = theme.accent;
  if (!dark) {
    return {
      bg1: n[50],
      bg2: n[100],
      bg3: "#ffffff",
      panel: "#ffffff",
      panel2: "#ffffff",
      panel3: n[100],
      bar: "#ffffff",
      border: n[200],
      border2: n[300],
      text: n[900],
      muted: n[500],
      accent: a[600],
      accent2: n[600],
      focus: withAlpha(a[500], 0.35),
      shadow: withAlpha(n[900], 0.1),
      onAccent: inkOn(a[600], n[950]),
    };
  }
  return {
    bg1: n[950],
    bg2: mix(n[950], "#000000", 0.25),
    bg3: n[900],
    panel: n[900],
    panel2: n[800],
    panel3: n[950],
    bar: n[900],
    border: n[700],
    border2: n[600],
    text: n[100],
    muted: n[400],
    accent: a[400],
    accent2: n[300],
    focus: withAlpha(a[400], 0.4),
    shadow: "rgba(0, 0, 0, 0.45)",
    onAccent: inkOn(a[400], n[950]),
  };
}

/* ---- stylesheet ---------------------------------------------------------- */

const ROLE_VARS: ReadonlyArray<[keyof ThemePalette, string]> = [
  ["bg1", "--waki-bg-1"],
  ["bg2", "--waki-bg-2"],
  ["bg3", "--waki-bg-3"],
  ["panel", "--waki-panel"],
  ["panel2", "--waki-panel-2"],
  ["panel3", "--waki-panel-3"],
  ["bar", "--waki-bar"],
  ["border", "--waki-border"],
  ["border2", "--waki-border-2"],
  ["text", "--waki-text"],
  ["muted", "--waki-muted"],
  ["accent", "--waki-accent"],
  ["accent2", "--waki-accent-2"],
  ["focus", "--waki-focus"],
  ["shadow", "--waki-shadow"],
  ["onAccent", "--waki-on-accent"],
];

function roleDecls(p: ThemePalette): string[] {
  return [
    ...ROLE_VARS.map(([k, v]) => `${v}: ${p[k]};`),
    // The names the app's own components read. Opaque on purpose: a mobile
    // list row must hide the swipe-action layer as it slides across it.
    `--wov-accent: ${p.accent};`,
    `--wov-accent-contrast: ${p.onAccent};`,
    `--wov-surface: ${p.panel};`,
    `--wov-bar: ${p.bar};`,
  ];
}

function scaleDecls(theme: ThemeDef): string[] {
  const triplet = (hex: string) => hexToRgb(hex).join(" ");
  return [
    ...SCALE_STEPS.map((s) => `--wov-n-${s}: ${triplet(theme.neutral[s])};`),
    ...SCALE_STEPS.map((s) => `--wov-a-${s}: ${triplet(theme.accent[s])};`),
  ];
}

function block(selector: string, decls: string[]): string {
  return `${selector} {\n${decls.map((d) => `  ${d}`).join("\n")}\n}`;
}

/** Every theme's variables as CSS.
 *
 *  The default theme also claims `:root` and bare `html.dark`, so the page is
 *  styled before `data-waki-theme` is set and in any context that never sets
 *  it. Specificity does the rest: `html.dark[data-waki-theme=x]` beats every
 *  light block, and each theme's light block beats the default's. */
export function themeStylesheet(): string {
  const out = ["/* Generated from src/lib/themes.ts (the Waki family themes). Edit waki-themes, not here. */"];
  for (const t of THEMES) {
    const isDefault = t.id === DEFAULT_THEME_ID;
    const attr = `[data-waki-theme="${t.id}"]`;
    out.push(
      block(isDefault ? `:root,\nhtml${attr}` : `html${attr}`, [
        ...scaleDecls(t),
        ...roleDecls(themePalette(t, false)),
      ]),
    );
    out.push(
      block(isDefault ? `html.dark,\nhtml.dark${attr}` : `html.dark${attr}`, roleDecls(themePalette(t, true))),
    );
    if (paintsCanvasGradient(t)) {
      // The canvas behind the panes (the seams and the title bar) is the
      // theme's own gradient; the selected nav row's edge is its amber.
      out.push(block(`html${attr} .wov-ground`, ["background: linear-gradient(135deg, var(--waki-bg-1), var(--waki-bg-2)) fixed;"]));
      out.push(block(`html${attr} [data-nav-row][aria-current]`, ["border-left-color: var(--waki-accent-2);"]));
    }
  }
  return out.join("\n\n") + "\n";
}
